// React/Next imports
import { useEffect, useRef, useMemo } from 'react';

// Hooks
import useSocialProfile from '@/utils/hooks/swr/useSocialProfile';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

//Utils
import { ONBOARDING_TASKS } from '@/components/composite/FirstStats/Content/const';

// Constants/Libs
import { onboardingTasks } from '@/lib/onboardingTasks/tasks';
import { firstStatsTasks } from '@/lib/firstStats/firstStatsTasks';
import { FIRST_STATS_STATUS } from '@/lib/firstStats/firstStatsStatus';

// Types
import type { FirstStatsTaskType } from '@/lib/firstStats/firstStatsTasks';
import type { StartButtonBlockProps } from '@/components/composite/FirstStats/Content/StartButtonBlock';
import type { FirstStatsStatusType } from '@/lib/firstStats/firstStatsStatus';
import type { TaskType } from '@/lib/onboardingTasks/tasks';

// Components
import Layout from '@/components/composite/FirstStats/Layout';
import Header from '@/components/composite/FirstStats/Content/Header';
import AvailableTasksList from '@/components/composite/FirstStats/Content/AvailableTasksList';
import RelaunchButtonBlock from '@/components/composite/FirstStats/Content/RelaunchButtonBlock';
import StartButtonBlock from '@/components/composite/FirstStats/Content/StartButtonBlock';

type OnboardingTaskType = {
    slug: string;
};

export type AvailableTaskType = TaskType & OnboardingTaskType & FirstStatsTaskType;

export type ContentProps = Pick<StartButtonBlockProps, 'start'>;

const Content = ({ start }: ContentProps) => {
    const { activeSocialProfile } = useActiveSocialProfile();

    const { socialProfile, mutateSocialProfile } = useSocialProfile({
        SPID: activeSocialProfile?.id
    });

    const availableTasks: AvailableTaskType[] = Object.keys(firstStatsTasks).map((taskSlug) => ({
        ...onboardingTasks[taskSlug],
        ...firstStatsTasks[taskSlug],
        ...ONBOARDING_TASKS?.find((task) => task.slug === taskSlug)
    }));

    const firstStatsStatus: FirstStatsStatusType = useMemo(() => {
        if (
            activeSocialProfile?.first_stats ||
            activeSocialProfile?.full_stats ||
            socialProfile?.first_stats ||
            socialProfile?.full_stats
        ) {
            return FIRST_STATS_STATUS.READY;
        }

        if (activeSocialProfile?.first_stats_relaunch || socialProfile?.first_stats_relaunch) {
            return FIRST_STATS_STATUS.RELAUNCH;
        }

        if (
            activeSocialProfile?.first_stats_relaunch_failed ||
            socialProfile?.first_stats_relaunch_failed
        ) {
            return FIRST_STATS_STATUS.RELAUNCH_FAILED;
        }

        return FIRST_STATS_STATUS.LOADING;
    }, [activeSocialProfile, socialProfile]);

    const timeoutId = useRef<NodeJS.Timeout>();

    useEffect(() => {
        clearTimeout(timeoutId.current);
        if (socialProfile && !socialProfile.first_stats && !socialProfile?.full_stats) {
            timeoutId.current = setTimeout(() => {
                mutateSocialProfile();
            }, 30000);
        }

        return () => clearTimeout(timeoutId.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socialProfile]);

    return (
        <Layout className="h-full w-full">
            <Layout.MainContent>
                <Header firstStatsStatus={firstStatsStatus} />
                {firstStatsStatus === FIRST_STATS_STATUS.RELAUNCH ? (
                    <RelaunchButtonBlock
                        socialProfile={socialProfile}
                        onRelaunch={mutateSocialProfile}
                    />
                ) : null}
                <AvailableTasksList tasks={availableTasks} />
                <div className="flex justify-end pt-10">
                    <StartButtonBlock firstStatsStatus={firstStatsStatus} start={start} />
                </div>
            </Layout.MainContent>
        </Layout>
    );
};

export default Content;
